/**
 * Checkout Initializer Composable
 *
 * Encapsulates checkout initialization logic from URL parameters.
 */

import { useCheckoutStore } from '@/stores/checkout';
import { parseCheckoutUrlParams } from '@/utils/checkout/checkoutParsing';
import type { CheckoutValidationResult } from '@/types/checkout';

/**
 * Composable for initializing checkout from URL parameters
 *
 * @example
 * ```typescript
 * const { initializeCheckout, canProceed } = useCheckoutInitializer();
 *
 * onMounted(async () => {
 *   const { isValid, errors } = await initializeCheckout();
 *
 *   if (!isValid) {
 *     console.error('Missing checkout parameters:', errors);
 *     // Handle error state
 *   }
 *
 *   if (canProceed()) {
 *     // Proceed with checkout
 *   }
 * });
 * ```
 */
export function useCheckoutInitializer() {
  const checkoutStore = useCheckoutStore();

  /**
   * Initialize checkout from URL parameters
   *
   * Process:
   * 1. Parse URL parameters
   * 2. Validate all required fields
   * 3. Set initial state in store
   * 4. Return validation result
   *
   * @returns Promise with validation result
   */
  async function initializeCheckout(): Promise<CheckoutValidationResult> {
    const parsedState = parseCheckoutUrlParams();

    checkoutStore.setInitialState(parsedState);

    const validation = checkoutStore.validateCheckout();

    if (!validation.isValid) {
      console.warn('[CheckoutInitializer] Validation errors:', validation.errors);
    }

    return validation;
  }

  function getFirstMissingParam(): string | null {
    return checkoutStore.firstMissingParam;
  }

  function canProceed(): boolean {
    return checkoutStore.hasAllRequiredParams && checkoutStore.hasValidDateRange;
  }

  function getMissingFields(): string[] {
    return checkoutStore.missingRequiredFields;
  }

  function hasParam(paramName: string): boolean {
    switch (paramName) {
      case 'offerIds':
        return checkoutStore.hasProductId;
      case 'productCode':
        return checkoutStore.hasProductCode;
      case 'roomCodes':
        return checkoutStore.hasRoomCode;
      case 'boardCode':
        return checkoutStore.hasBoardCode;
      case 'serviceVariantIds':
        return checkoutStore.hasServiceVariantId;
      case 'checkIn':
        return checkoutStore.hasCheckIn;
      case 'checkOut':
        return checkoutStore.hasCheckOut;
      case 'pax':
        return checkoutStore.hasPassengers;
      default:
        return false;
    }
  }

  return {
    initializeCheckout,
    getFirstMissingParam,
    canProceed,
    getMissingFields,
    hasParam,
  };
}
